import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowDownToLine,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Package,
  Server,
  ExternalLink,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  agentUpdatesApi,
  type AgentVersionInfo,
  type UpdateResult,
} from "@/api/agent-updates";

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  active: "success",
  running: "success",
  idle: "secondary",
  paused: "warning",
  error: "danger",
  terminated: "danger",
  pending_approval: "warning",
};

const stepLabels: Record<string, string> = {
  backup_config: "Backup config",
  backup_workspace: "Backup workspace",
  npm_update: "Install update",
  verify_version: "Verify version",
  restore_config: "Restore config",
  restore_workspace: "Restore workspace",
  restart_gateway: "Restart gateway",
};

export function AgentUpdates() {
  const queryClient = useQueryClient();
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [updateResults, setUpdateResults] = useState<Record<string, UpdateResult>>({});

  // Fetch latest version
  const {
    data: latestVersion,
    isLoading: loadingVersion,
    refetch: refetchVersion,
  } = useQuery({
    queryKey: ["agent-updates", "latest-version"],
    queryFn: agentUpdatesApi.getLatestVersion,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch all agent versions
  const {
    data: agentVersions = [],
    isLoading: loadingAgents,
    refetch: refetchAgents,
  } = useQuery({
    queryKey: ["agent-updates", "agents"],
    queryFn: agentUpdatesApi.listAgentVersions,
    staleTime: 30 * 1000,
  });

  // Check all versions mutation
  const checkAllMutation = useMutation({
    mutationFn: agentUpdatesApi.checkAllVersions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-updates", "agents"] });
    },
  });

  // Check single agent version
  const checkVersionMutation = useMutation({
    mutationFn: (agentId: string) => agentUpdatesApi.checkAgentVersion(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-updates", "agents"] });
    },
  });

  // Update agent mutation. The server runs the update detached from the
  // HTTP request, so we poll the status endpoint and stream progress into
  // updateResults so the expanded row shows step-by-step output live.
  const updateMutation = useMutation({
    mutationFn: (agentId: string) =>
      agentUpdatesApi.updateAgent(agentId, {
        onProgress: (job) => {
          setUpdateResults((prev) => ({
            ...prev,
            [agentId]: {
              success: job.status === "success",
              agentId: job.agentId,
              previousVersion: job.previousVersion,
              newVersion: job.newVersion,
              steps: job.steps,
              error: job.error,
            },
          }));
        },
      }),
    onSuccess: (result) => {
      setUpdateResults((prev) => ({ ...prev, [result.agentId]: result }));
      queryClient.invalidateQueries({ queryKey: ["agent-updates", "agents"] });
    },
    onError: (_err, agentId) => {
      setUpdateResults((prev) => ({
        ...prev,
        [agentId]: {
          success: false,
          agentId,
          previousVersion: null,
          newVersion: null,
          steps: [],
          error: _err instanceof Error ? _err.message : "Update failed",
        },
      }));
    },
  });

  // Auto-check versions once on first load if any agents show "Unknown"
  const hasAutoChecked = useRef(false);
  useEffect(() => {
    if (
      !hasAutoChecked.current &&
      !loadingAgents &&
      agentVersions.length > 0 &&
      agentVersions.some((a) => !a.currentVersion)
    ) {
      hasAutoChecked.current = true;
      checkAllMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAgents, agentVersions]);

  const outdatedCount = agentVersions.filter((a) => a.updateAvailable).length;
  const activeAgents = agentVersions.filter(
    (a) => a.status === "active" || a.status === "idle" || a.status === "running",
  );

  return (
    <CenterPanel title="Agent Updates">
      {/* Latest version card */}
      <Card className="mb-6">
        <CardContent className="py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10">
                <Package size={24} className="text-accent" />
              </div>
              <div>
                <p className="text-sm text-text-muted">Latest OpenClaw Version</p>
                {loadingVersion ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Loader2 size={14} className="animate-spin text-text-muted" />
                    <span className="text-sm text-text-secondary">Checking...</span>
                  </div>
                ) : latestVersion ? (
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xl font-semibold text-text-primary font-mono">
                      v{latestVersion.version}
                    </span>
                    <Badge variant={latestVersion.source === "cache" ? "secondary" : "success"}>
                      {latestVersion.source}
                    </Badge>
                    {latestVersion.publishedAt && (
                      <span className="text-xs text-text-muted">
                        Released {new Date(latestVersion.publishedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-danger">Failed to fetch</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {latestVersion?.htmlUrl && (
                <a
                  href={latestVersion.htmlUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                >
                  View release <ExternalLink size={12} />
                </a>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchVersion()}
                disabled={loadingVersion}
              >
                <RefreshCw size={14} className={loadingVersion ? "animate-spin" : ""} />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary + actions */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-text-secondary">
            {agentVersions.length} agent{agentVersions.length !== 1 ? "s" : ""} tracked
          </p>
          {outdatedCount > 0 && (
            <Badge variant="warning">
              {outdatedCount} update{outdatedCount !== 1 ? "s" : ""} available
            </Badge>
          )}
          {outdatedCount === 0 && agentVersions.length > 0 && (
            <Badge variant="success">All up to date</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => checkAllMutation.mutate()}
            disabled={checkAllMutation.isPending}
          >
            {checkAllMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            Check All Versions
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetchAgents();
              refetchVersion();
            }}
            disabled={loadingAgents}
          >
            <RefreshCw size={14} className={loadingAgents ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Agent list */}
      {loadingAgents ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-text-muted" />
          </CardContent>
        </Card>
      ) : agentVersions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Server size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">
              No OpenClaw agents found. Deploy agents from within a tenant workspace.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {agentVersions.map((agent) => (
            <AgentRow
              key={agent.agentId}
              agent={agent}
              expanded={expandedAgent === agent.agentId}
              onToggle={() =>
                setExpandedAgent(expandedAgent === agent.agentId ? null : agent.agentId)
              }
              updateResult={updateResults[agent.agentId]}
              isUpdating={updateMutation.isPending && updateMutation.variables === agent.agentId}
              isChecking={checkVersionMutation.isPending && checkVersionMutation.variables === agent.agentId}
              onCheckVersion={() => checkVersionMutation.mutate(agent.agentId)}
              onUpdate={() => {
                setExpandedAgent(agent.agentId);
                updateMutation.mutate(agent.agentId);
              }}
            />
          ))}
        </div>
      )}

      {/* Release notes */}
      {latestVersion?.releaseNotes && (
        <Card className="mt-6">
          <CardContent className="py-4">
            <h3 className="text-sm font-medium text-text-primary mb-2">Release Notes</h3>
            <pre className="text-xs text-text-secondary whitespace-pre-wrap break-words font-mono bg-surface-hover rounded-lg p-3 max-h-60 overflow-auto">
              {latestVersion.releaseNotes}
            </pre>
          </CardContent>
        </Card>
      )}
    </CenterPanel>
  );
}

function AgentRow({
  agent,
  expanded,
  onToggle,
  updateResult,
  isUpdating,
  isChecking,
  onCheckVersion,
  onUpdate,
}: {
  agent: AgentVersionInfo;
  expanded: boolean;
  onToggle: () => void;
  updateResult?: UpdateResult;
  isUpdating: boolean;
  isChecking: boolean;
  onCheckVersion: () => void;
  onUpdate: () => void;
}) {
  const isActive = agent.status === "active" || agent.status === "idle" || agent.status === "running";

  return (
    <Card>
      <CardContent className="py-0">
        {/* Main row */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
              <Server size={20} className="text-accent" />
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">{agent.agentName}</p>
              <p className="text-xs text-text-muted">
                {agent.companyName} · {agent.ipAddress || "No IP"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Version display */}
            <div className="text-right mr-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-text-muted">Current:</span>
                <span className="font-mono text-sm text-text-primary">
                  {agent.currentVersion ? `v${agent.currentVersion}` : "Unknown"}
                </span>
              </div>
              {agent.updateAvailable && agent.currentVersion && (
                <div className="flex items-center gap-1 mt-0.5">
                  <ArrowDownToLine size={10} className="text-warning" />
                  <span className="text-[11px] text-warning">
                    v{agent.latestVersion} available
                  </span>
                </div>
              )}
            </div>

            {/* Status badge */}
            <Badge variant={statusVariant[agent.status] ?? "default"}>
              {agent.status}
            </Badge>

            {/* Version status badge */}
            {agent.currentVersion && !agent.updateAvailable && (
              <Badge variant="success" className="whitespace-nowrap">
                <CheckCircle2 size={12} className="mr-1 shrink-0" />
                Up to date
              </Badge>
            )}
            {agent.updateAvailable && (
              <Badge variant="warning" className="whitespace-nowrap">
                <AlertCircle size={12} className="mr-1 shrink-0" />
                Update available
              </Badge>
            )}

            {/* Actions */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onCheckVersion}
              disabled={isChecking || !isActive}
              title="Check installed version"
            >
              {isChecking ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
            </Button>

            <Button
              variant={agent.updateAvailable ? "default" : "outline"}
              size="sm"
              onClick={onUpdate}
              disabled={isUpdating || !isActive || !agent.updateAvailable}
              title={!isActive ? "Agent must be active to update" : "Update OpenClaw"}
            >
              {isUpdating ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <ArrowDownToLine size={14} />
                  Update
                </>
              )}
            </Button>

            {/* Expand/collapse */}
            <Button variant="ghost" size="icon" onClick={onToggle}>
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          </div>
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="border-t border-border py-4">
            {updateResult ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  {updateResult.success ? (
                    <CheckCircle2 size={16} className="text-success" />
                  ) : (
                    <AlertCircle size={16} className="text-danger" />
                  )}
                  <span className="text-sm font-medium text-text-primary">
                    {updateResult.success ? "Update completed" : "Update failed"}
                  </span>
                  {updateResult.previousVersion && updateResult.newVersion && (
                    <span className="text-xs text-text-muted">
                      v{updateResult.previousVersion} → v{updateResult.newVersion}
                    </span>
                  )}
                </div>
                {updateResult.error && (
                  <p className="text-xs text-danger mb-3">{updateResult.error}</p>
                )}
                <div className="space-y-1.5">
                  {updateResult.steps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {step.success ? (
                        <CheckCircle2 size={12} className="text-success shrink-0" />
                      ) : (
                        <AlertCircle size={12} className="text-danger shrink-0" />
                      )}
                      <span className="text-text-secondary font-medium w-32">
                        {stepLabels[step.step] || step.step}
                      </span>
                      <span className="text-text-muted truncate">
                        {step.error || step.output || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-xs text-text-muted">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-text-secondary">Agent ID:</span>{" "}
                    <span className="font-mono">{agent.agentId.slice(0, 8)}...</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Tenant:</span> {agent.companyName}
                  </div>
                  <div>
                    <span className="text-text-secondary">IP Address:</span>{" "}
                    <span className="font-mono">{agent.ipAddress || "—"}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Last Checked:</span>{" "}
                    {agent.lastCheckedAt
                      ? new Date(agent.lastCheckedAt).toLocaleString()
                      : "Never"}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
